import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Link, useLocation } from 'react-router-dom';

import useFetch from "../../hooks/useFetch";
import { GenerateMarksTrackerCode } from "../../components/GenerateThink5whysCode";
import "./styles/AICounselling.css";

const MarksTracker = () => {
  const { user, token } = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [strongestSubject, setStrongestSubject] = useState(null);
  const [weakestSubject, setWeakestSubject] = useState(null);

  const cbseSubjects = [
    "English", "Hindi", "Mathematics", "Science", "Social Science", "Physics",
    "Chemistry", "Biology", "Computer Science", "Geography", "History",
    "Economics", "Civics", "Physical Education", "Fine Arts", "Sanskrit",
    "Environmental Science", "Commercial Studies", "Commercial Applications",
    "Art", "Music", "Home Science", "Physical Science", "Political Science",
    "Accountancy", "Business Studies", "Applied Mathematics",
    "Entrepreneurship", "Psychology", "Sociology", "Legal Studies",
    "Engineering Graphics", "Biotechnology", "Multimedia and Web Technology",
    "Informatics Practices"
  ];
  const [availableSubjects, setAvailableSubjects] = useState(cbseSubjects);

  const [formData, setFormData] = useState({
    userId: user._id,
    userCode: user.think5whyscode,
    userName: user.name,
    marksTrackerCode: GenerateMarksTrackerCode(),
    examTitle: "",
    subjects: [],
    strongest: weakestSubject,
    weakest: strongestSubject,
    remarks: "",
    examDate: null,
    createdBy: user._id,
    dateCreated: new Date(),
    isActive: true,
    isDeleted: false,
  });

  const updateSubjects = (index, key, value) => {
    setFormData((prev) => {
      const updatedSubjects = [...prev.subjects];
      updatedSubjects[index][key] = value;

      if (key === "fullmarks" || key === "marksobtained") {
        const { fullmarks, marksobtained } = updatedSubjects[index];
        if (fullmarks && marksobtained) {
          updatedSubjects[index].percentmarks = ((marksobtained / fullmarks) * 100).toFixed(2);
        }
      }

      return { ...prev, subjects: updatedSubjects };
    });
  };

  const addSubject = () => {
    setFormData((prev) => ({
      ...prev,
      subjects: [...prev.subjects, { name: "", marksobtained: "", fullmarks: "", percentmarks: "" }],
    }));
  };

  const removeSubject = (index) => {
    setFormData((prev) => {
      const updatedSubjects = prev.subjects.filter((_, i) => i !== index);
      setAvailableSubjects([...availableSubjects, prev.subjects[index].name].filter(Boolean));
      return { ...prev, subjects: updatedSubjects };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveMarksTracker = async () => {
    for (const subject of formData.subjects) {
        if (!subject.name.trim()) {
            alert("Subject name cannot be blank!");
            return;
        }
        if (subject.fullMarks <= 0) {
            alert("Full marks must be greater than 0 to avoid divide-by-zero errors.");
            return;
        }
    }
    if (formData.remarks.length >= 500) {
      alert("Remarks must be less than 500 characters.");
      return;
    }
    // setLoading(true);
    try {
      const config = {
        url: `/markstracker/save`,
        method: "post",
        data: formData,
        headers: { Authorization: token },
      };
      await fetchData(config);
      navigate("/marks-tracker-list");
    } catch (error) {
      console.error("Error saving marks tracker:", error);
    }
  };

  useEffect(() => {
    if (formData.subjects.length) {
      const strongest = formData.subjects.reduce((max, subject) =>
        subject.percentmarks > max.percentmarks ? subject : max, { percentmarks: -1 });
      const weakest = formData.subjects.reduce((min, subject) =>
        subject.percentmarks < min.percentmarks ? subject : min, { percentmarks: 101 });

      setStrongestSubject(strongest.name);
      setWeakestSubject(weakest.name);
    } else {
      setStrongestSubject(null);
      setWeakestSubject(null);
    }
  }, [formData.subjects]);

  return (
    <div className="min-h-screen bg-black py-2">
      <div className='border-b border-blue-400 mb-2'>
          <h2 className="text-lg lg:text-xl font-extrabold text-center text-yellow-200 mb-2 p-2">
              📖 Academic Marks Tracker
          </h2>
      </div>
      <div className="my-4 mx-4 min-h-[85vh] flex flex-col justify-center">
        {/* <div className="section border-2 border-green-300 rounded-lg shadow-lg px-6 py-6 bg-gradient-to-r from-gray-700 to-gray-800 text-white"> */}
          <p className="text-white text-m">Academic Marks Tracker Code: 
              <span className="text-orange-400">{formData.marksTrackerCode}</span>
          </p>

          <div className="form-group mb-6">
            <label htmlFor="examTitle" className="block text-sm font-semibold mb-2">Academic Exam Title:</label>
            <input
              type="text"
              id="examTitle"
              name="examTitle"
              placeholder="Your exam title..."
              style={{width: "100%"}}
              className="w-full max-w-md px-4 py-2 border-2 border-white rounded-md bg-gray-100 text-black"
              value={formData.examTitle}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group mb-4">
            <label htmlFor="examDate" className="block text-sm font-semibold mb-2">Exam Date:</label>
            <input
              type="date"
              id="examDate"
              name="examDate"
              style={{width: "150px"}}
              className="w-full max-w-md px-4 py-2 border-2 border-white rounded-md bg-gray-100 text-black"
              value={formData.examDate || ""}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group mb-4">
            <label className="block text-sm font-semibold mb-2">Subjects:</label>
            {formData.subjects.map((subject, index) => (
              <div key={index} className="flex flex-wrap gap-4 items-center mb-4">
                <select
                  value={subject.name || ""}
                  onChange={(e) => updateSubjects(index, "name", e.target.value)}
                  style={{width: "250px"}}
                  className="h-12 px-4 py-2 border-2 border-white rounded-md bg-gray-100 text-black"
                >
                  <option value="" disabled>Select Subject</option>
                  {availableSubjects.map((name, idx) => (
                    <option key={idx} value={name}>{name}</option>
                  ))}
                </select>

                <input
                  type="number"
                  placeholder="Marks Obtained"
                  value={subject.marksobtained || ""}
                  onChange={(e) => updateSubjects(index, "marksobtained", e.target.value)}
                  style={{width: "100px"}}
                  className="h-12 px-4 py-2 border-2 border-white rounded-md bg-gray-100 text-black"
                />

                <input
                  type="number"
                  placeholder="Full Marks"
                  value={subject.fullmarks || ""}
                  onChange={(e) => updateSubjects(index, "fullmarks", e.target.value)}
                  style={{width: "100px"}}
                  className="h-12 px-4 py-2 border-2 border-white rounded-md bg-gray-100 text-black"
                />

                  <button
                      className="h-12 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                      onClick={() => removeSubject(index)}
                  >
                      Remove
                  </button>
                </div>
              ))}
              <button
                  className="h-12 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                  onClick={addSubject}
              >
                  Add Subject
              </button>
            </div>

            <div className="mb-6">
                <p><strong>Strongest Subject:</strong> {strongestSubject || "N/A"}</p>
                <p><strong>Weakest Subject:</strong> {weakestSubject || "N/A"}</p>
            </div>

            <div className="form-group mb-6">
                <label htmlFor="remarks" className="block text-sm font-semibold mb-2">Remarks:</label>
                <textarea
                    id="remarks"
                    name="remarks"
                    placeholder="Add remarks here..."
                    value={formData.remarks}
                    onChange={handleInputChange}
                    style={{width: "100%", height: "100px"}}
                    className="w-full px-4 py-2 border-2 border-white rounded-md bg-gray-100 text-black"
                />
            </div>

            <div className="flex justify-end gap-4">
                <button
                    className="h-12 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    onClick={saveMarksTracker}
                    disabled={loading}
                >
                    {loading ? "Saving..." : "Save Marks Tracker"}
                </button>
                <Link
                    to="/marks-tracker-list"
                    className="h-12 px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                    Cancel
                </Link>
            </div>
          {/* </div> */}
        </div>
        <footer className="bg-black border-t border-blue-400 text-gray-400 py-4">
          <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/about-us" className="hover:text-teal-300">About</Link>
              <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
              <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
              <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
            </div>
          </div>
      </footer>  
    </div>
  );
};

export default MarksTracker;
